import PropTypes from 'prop-types';
import { useDataProvider, useNotify, useTranslate } from 'react-admin';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@mui/material';
import InfoIcon from '@/assets/icons/info-icon.svg?react';

const CachedDataActionButton = ({ resource, type }) => {
    const dataProvider = useDataProvider();
    const [loading, setLoading] = useState(true);
    const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
    const notify = useNotify();
    const translate = useTranslate();
    const cacheRef = useRef({ value: null, rawDate: null });

    const fetchLastUpdatedAt = useCallback(async () => {
        try {
            const result = await dataProvider.get(`${resource}/cached-datetime`);
            const { date, timezone } = result?.data ?? {};
            const rawDate = date ? new Date(`${date.replace(' ', 'T')}${timezone}`) : null;
            const time = rawDate ? rawDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : null;
            cacheRef.current = { value: time, rawDate };
            setLastUpdatedAt(time);
        } catch (error) {
            notify(error.message, { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [dataProvider, notify, resource]);

    useEffect(() => {
        fetchLastUpdatedAt();
    }, [fetchLastUpdatedAt]);

    const submit = async () => {
        const { rawDate } = cacheRef.current;
        if (rawDate) {
            const minutesAgo = Math.floor((Date.now() - rawDate.getTime()) / 60_000);
            if (minutesAgo < 15) {
                const minutesLeft = 15 - minutesAgo;
                notify(translate('common.validation.cachedDataCooldown', { minutesAgo, minutesLeft }), { type: 'warning' });
                return;
            }
        }
        setLoading(true);
        try {
            await dataProvider.post(`${resource}/cached-data`, {});
            await fetchLastUpdatedAt();
        } catch (error) {
            notify(error.message, { type: 'warning' });
        } finally {
            setLoading(false);
        }
    };

    let translationKey = `common.cachedDataLastUpdatedAt`;
    if (type) {
        translationKey = `${translationKey}${type}`;
    }
    const buttonLabel = translate(translationKey, { lastUpdatedAt: lastUpdatedAt });

    return (
        <Button
            color='primary'
            size='small'
            startIcon={<InfoIcon />}
            onClick={submit}
            disabled={loading}
            aria-label={buttonLabel}
            sx={{ whiteSpace: 'break-spaces' }}
        >
            {loading ? translate('common.loading') : buttonLabel}
        </Button>
    );
};

CachedDataActionButton.propTypes = {
    resource: PropTypes.string.isRequired,
    type: PropTypes.string,
};

export default CachedDataActionButton;
